import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // useNavigate 임포트
import { getStaticFileUrl } from "../utils/config";

interface PortfolioCard {
  id: string;
  imgSrc: string;
  logoSrc: string;
  title: string;
  description: string;
}

interface PortfolioData {
  title: string;
  description: string;
  cards: PortfolioCard[];
}

const Portfolio = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const navigate = useNavigate(); // navigate 함수 생성

  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    title: 'PORTFOLIO',
    description: '대표적인 성공사례를 소개합니다.',
    cards: [
      {
        id: '1',
        imgSrc: getStaticFileUrl('/img/home/SBS생방송투데이.png'),
        logoSrc: getStaticFileUrl('/img/logo/sbs.png'),
        title: 'SBS',
        description: 'SBS 생방송투데이',
      },
      {
        id: '2',
        imgSrc: getStaticFileUrl('/img/home/SBS꾸러기탐구생활.png'),
        logoSrc: getStaticFileUrl('/img/logo/sbs.png'),
        title: 'SBS',
        description: 'SBS 꾸러기탐구생활',
      },
      {
        id: '3',
        imgSrc: getStaticFileUrl('/img/home/SBS물은생명이다.png'),
        logoSrc: getStaticFileUrl('/img/logo/sbs.png'),
        title: 'SBS',
        description: 'SBS 물은 생명이다',
      },
    ],
  });

  // 카드의 고정 높이 (반응형으로 조정)
  const cardHeight = isMobile ? "300px" : "400px";

  // API에서 포트폴리오 데이터 불러오기
  useEffect(() => {
    const loadPortfolioData = async () => {
      try {
        const { portfolioApi } = await import('../services/api');
        const data = await portfolioApi.getHomePortfolioData();
        
        console.log('API 응답 데이터:', data);
        if (data && data.headingTitle) {
          setPortfolioData({
            title: data.headingTitle,
            description: `${data.subLine1} ${data.subLine2}`,
            cards: Array.isArray(data.cards) && data.cards.length > 0 ? data.cards : portfolioData.cards
          });
        }
      } catch (error) {
        console.error('포트폴리오 데이터 로드 실패:', error);
      }
    };

    loadPortfolioData();
  }, []);

  // 부드러운 자동 스크롤 (requestAnimationFrame)
  useEffect(() => {
    if (!isAutoScrolling) return;

    let previousTimestamp: number | null = null;
    let animationFrameId: number;
    const speed = 0.15;

    const step = (timestamp: number) => {
      if (previousTimestamp !== null && scrollRef.current) {
        const delta = timestamp - previousTimestamp;
        scrollRef.current.scrollLeft += delta * speed;
        const { scrollWidth, clientWidth } = scrollRef.current;
        if (scrollRef.current.scrollLeft >= scrollWidth - clientWidth) {
          scrollRef.current.scrollLeft = 0;
        }
      }
      previousTimestamp = timestamp;
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrameId);
  }, [isAutoScrolling, isMobile, isTablet]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setIsAutoScrolling(false);
    setStartX(event.pageX - (scrollRef.current?.offsetLeft ?? 0));
    setInitialScrollLeft(scrollRef.current?.scrollLeft ?? 0);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    event.preventDefault();
    const x = event.pageX - (scrollRef.current?.offsetLeft ?? 0);
    const walk = (x - startX) * 2;
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = initialScrollLeft - walk;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsAutoScrolling(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "80vh",
        overflow: "hidden",
        marginBottom: "3rem",
      }}
    >
      {/* 텍스트 섹션 */}
      <Box sx={{ textAlign: "center", padding: "3rem 2rem" }}>
        <Typography
          sx={{
            fontSize: isMobile ? "2rem" : "2.75rem",
            fontWeight: "700",
            marginBottom: "10px",
            color: "#E23C3C",
          }}
        >
          {portfolioData.title}
        </Typography>
        <Typography
          sx={{ fontSize: isMobile ? "1rem" : "1.25rem", textAlign: "center" }}
        >
          {portfolioData.description}
        </Typography>
      </Box>

      {/* 카드 섹션 */}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          p: isMobile ? "1rem 0.5rem" : "1rem 2rem",
          gap: "1rem",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          alignItems: "center",
          width: "100%",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {portfolioData.cards.map((card, index) => (
          <Box
            key={card.id}
            sx={{
              position: "relative",
              background: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: isMobile ? "90%" : isTablet ? "50%" : "25%",
              height: cardHeight,
              scrollSnapAlign: "start",
              transition: "transform 0.3s ease-in-out",
              "&:hover .overlay, &:hover .logo": {
                opacity: 1,
              },
            }}
          >
            {/* 이미지 영역 (16:9 비율 적용) */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "16/9",
                overflow: "hidden",
              }}
            >
              <img
                src={getStaticFileUrl(card.imgSrc)}
                alt={card.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "opacity 0.3s ease-in-out",
                }}
              />

              {/* 어두운 오버레이 */}
              <Box
                className="overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  opacity: 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
              />

              {/* 로고 이미지 영역 */}
              <Box
                className="logo"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "15rem",
                  height: "3.25rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  opacity: 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
              >
                <img
                  src={getStaticFileUrl(card.logoSrc)}
                  alt={card.title}
                  style={{
                    height: "100%",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
            {/* 텍스트 영역 */}
            <Box
              sx={{
                height: "20%",
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{ fontSize: "1.5rem", fontWeight: "400", color: "#333", lineHeight: "1.5" }}
              >
                {card.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* 자세히 알아보기 버튼 */}
      <Button
        onClick={() => {
          navigate("/portfolio");
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }, 100); // 100ms 정도 지연
        }}
        sx={{
          mt: 3,
          px: 4,
          py: 1,
          border: "0.1rem solid #d82828",
          borderRadius: "1.875rem",
          fontSize: isMobile ? "0.75rem" : "1rem",
          fontWeight: "400",
          color: "#d82828",
          textTransform: "none",
          transition: "all 0.3s",
          "&:hover": { backgroundColor: "#d82828", color: "#fff" },
        }}
      >
        자세히 알아보기
      </Button>
    </Box>
  );
};

export default Portfolio;
